/* A freshly generated API key, shown once and not dismissable by accident: no route will
 * return it again, so a key lost here has to be rotated. Shared by the two pages that produce
 * one — registering an integration, and rotating its key.
 */

import { useState } from 'react';
import { Button } from '@nextleap-al/admin-ui';

export interface IssuedKey {
  /** What the key belongs to — the integration's name. Named after the system while a
   *  product had exactly one registration; with several, the product no longer says which
   *  of them this key is for. */
  systemKey: string;
  value: string;
  rotated: boolean;
}

export function KeyBox({ issued, onDismiss }: { issued: IssuedKey; onDismiss: () => void }) {
  const [copyLabel, setCopyLabel] = useState('Copy');

  async function copy() {
    try {
      await navigator.clipboard.writeText(issued.value);
      setCopyLabel('Copied');
    } catch {
      // Clipboard access is refused on plain http in some browsers. Selecting the text is
      // the fallback that always works, and saying so beats a button that does nothing.
      setCopyLabel('Copy it by hand — the browser refused clipboard access');
    }
  }

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--warning)] bg-[var(--warning-light)] p-4">
      <p className="text-sm font-semibold text-[var(--text-primary)]">
        {issued.rotated ? 'New key' : 'Key'} for <b>{issued.systemKey}</b> — shown once
      </p>
      <code className="mt-2 block break-all rounded bg-[var(--bg-elevated)] px-2 py-1.5 font-mono text-xs">
        {issued.value}
      </code>
      <p className="mt-2 max-w-prose text-sm text-[var(--text-secondary)]">
        Paste it into that application's configuration as its assistant signing secret
        {issued.rotated ? ' now — tokens signed with the old key are already being refused' : ''}. It
        is never shown again, and it must never reach a browser: the host signs tokens with it
        server-side, and only the signature travels.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={copy}>
          {copyLabel}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          I have saved it
        </Button>
      </div>
    </div>
  );
}
